// Security policy of a workspace (X2-05): who must use two-factor, which networks may sign in, how long a session lives,
// and single sign-on. Stored as the workspace setting `security` (owners and admins write it; the server reads it on
// every request, apps/server/src/session.ts). CIDR helpers live here because the web checks a list before saving it and
// the server enforces it.
import { z } from "zod";

/** A network the workspace allows: an IPv4 or IPv6 address, or a range in CIDR form (203.0.113.0/24, 2001:db8::/32). */
export type ParsedCidr = { version: 4 | 6; base: bigint; bits: number; text: string };

const V4 = /^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})$/;

/** An IP address as a number and its version. Accepts IPv4, IPv6 (with `::`), and IPv4-mapped IPv6 (read as IPv4). */
export function parseIp(text: string): { version: 4 | 6; value: bigint } | null {
  const ip = text.trim().replace(/^\[|\]$/g, "").replace(/%.*$/, "");
  const v4 = V4.exec(ip);
  if (v4) {
    const parts = v4.slice(1).map(Number);
    if (parts.some((p) => p > 255)) return null;
    return { version: 4, value: parts.reduce((acc, p) => (acc << 8n) | BigInt(p), 0n) };
  }
  if (!ip.includes(":")) return null;
  // IPv4 tail (::ffff:1.2.3.4)
  let head = ip;
  let tail: bigint[] = [];
  const dotted = /(\d{1,3}(?:\.\d{1,3}){3})$/.exec(ip);
  if (dotted) {
    const v = parseIp(dotted[1]!);
    if (!v || v.version !== 4) return null;
    tail = [(v.value >> 16n) & 0xffffn, v.value & 0xffffn];
    head = ip.slice(0, ip.length - dotted[1]!.length) + "0:0";
  }
  const halves = head.split("::");
  if (halves.length > 2) return null;
  const groups = (s: string) => (s === "" ? [] : s.split(":"));
  const left = groups(halves[0]!);
  const right = halves.length === 2 ? groups(halves[1]!) : [];
  if (halves.length === 1 && left.length !== 8) return null;
  if (halves.length === 2 && left.length + right.length > 7) return null;
  const fill = halves.length === 2 ? new Array<string>(8 - left.length - right.length).fill("0") : [];
  const all = [...left, ...fill, ...right];
  if (all.length !== 8 || all.some((g) => !/^[0-9a-fA-F]{1,4}$/.test(g))) return null;
  const nums = all.map((g) => BigInt(`0x${g}`));
  if (dotted) {
    nums[6] = tail[0]!;
    nums[7] = tail[1]!;
  }
  const value = nums.reduce((acc, n) => (acc << 16n) | n, 0n);
  // ::ffff:a.b.c.d is an IPv4 client seen through a dual-stack socket
  if (value >> 32n === 0xffffn) return { version: 4, value: value & 0xffffffffn };
  return { version: 6, value };
}

/** A CIDR range (or a single address, which is /32 or /128), or null when it does not parse. */
export function parseCidr(text: string): ParsedCidr | null {
  const raw = text.trim();
  const [addr, len, extra] = raw.split("/");
  if (extra !== undefined || !addr) return null;
  const ip = parseIp(addr);
  if (!ip) return null;
  const max = ip.version === 4 ? 32 : 128;
  let bits = max;
  if (len !== undefined) {
    if (!/^\d{1,3}$/.test(len)) return null;
    bits = Number(len);
    if (bits > max) return null;
  }
  const shift = BigInt(max - bits);
  return { version: ip.version, base: (ip.value >> shift) << shift, bits, text: raw };
}

/** Whether the address is inside the range. */
export function inCidr(ip: string, range: ParsedCidr): boolean {
  const p = parseIp(ip);
  if (!p || p.version !== range.version) return false;
  const shift = BigInt((range.version === 4 ? 32 : 128) - range.bits);
  return (p.value >> shift) << shift === range.base;
}

/** Whether the address is inside any of the ranges (ranges that do not parse match nothing). */
export function ipAllowed(ip: string | undefined | null, cidrs: readonly string[]): boolean {
  if (!ip) return false;
  return cidrs.some((c) => {
    const range = parseCidr(c);
    return range ? inCidr(ip, range) : false;
  });
}

const CIDR = z.string().trim().min(2).max(60).refine((c) => parseCidr(c) !== null, "Use an address such as 203.0.113.7 or a range such as 203.0.113.0/24.");

/** Roles are named by id, as in the roles collection. */
const ROLE_ID = z.string().regex(/^role-[a-z0-9-]{1,60}$/);

/** The workspace security setting (Settings → Security). Defaults apply until an owner or admin saves one. */
export const SECURITY = z.strictObject({
  /** Roles whose members must have two-factor sign-in turned on before they can use the app (empty: nobody is required). */
  twoFactor: z.strictObject({ roles: z.array(ROLE_ID).max(60) }).default({ roles: [] }),
  /** Only these networks may use the app as staff or freelancers (portal visitors and public links are not affected). */
  ipAllowlist: z.strictObject({ enabled: z.boolean(), cidrs: z.array(CIDR).max(100) }).default({ enabled: false, cidrs: [] }),
  /** How long a session lasts in hours (from sign-in), and how many minutes without activity end it (0: no idle limit). */
  session: z.strictObject({ maxHours: z.number().int().min(1).max(2160), idleMinutes: z.number().int().min(0).max(1440) }).default({ maxHours: 720, idleMinutes: 0 }),
});
export type Security = z.infer<typeof SECURITY>;
export const DEFAULT_SECURITY: Security = { twoFactor: { roles: [] }, ipAllowlist: { enabled: false, cidrs: [] }, session: { maxHours: 720, idleMinutes: 0 } };

/** Single sign-on (OpenID Connect) for the workspace. The client secret is not here: it is sealed on the server and
 *  never read back. `required` turns off password and magic-link sign-in for people at the listed domains. */
export const SSO = z.strictObject({
  enabled: z.boolean(),
  issuer: z.url().max(300).or(z.literal("")),
  clientId: z.string().trim().max(200),
  /** Email domains that sign in through the provider (example.com). */
  domains: z.array(z.string().trim().toLowerCase().regex(/^[a-z0-9.-]+\.[a-z]{2,}$/)).max(20),
  required: z.boolean(),
  /** Create the person on first sign-in with a matching domain. They join with this role. */
  provision: z.strictObject({ enabled: z.boolean(), roleId: ROLE_ID.or(z.literal("")) }),
  /** oidc (the fields above) or saml (the fields in `saml`, read from the provider's metadata). */
  protocol: z.enum(["oidc", "saml"]).default("oidc"),
  /** SAML only. The provider's entity id, its single sign-on address and its signing certificate (PEM). Public values. */
  saml: z
    .strictObject({ idpEntityId: z.string().trim().max(500), ssoUrl: z.url().max(500).or(z.literal("")), cert: z.string().max(10_000) })
    .default({ idpEntityId: "", ssoUrl: "", cert: "" }),
  /** SCIM groups the agency maps to a role (X21-OPS-17). A group name is matched without case. Owner and admin are never mapped. */
  groupRoles: z.array(z.strictObject({ group: z.string().trim().min(1).max(200), roleId: ROLE_ID })).max(50).default([]),
});
export type Sso = z.infer<typeof SSO>;
export const DEFAULT_SSO: Sso = {
  enabled: false, issuer: "", clientId: "", domains: [], required: false, provision: { enabled: false, roleId: "" },
  protocol: "oidc", saml: { idpEntityId: "", ssoUrl: "", cert: "" }, groupRoles: [],
};

/** What the security policy decides about a request, in order: network, session age, idle time, two-factor. */
export type SecurityVerdict =
  | { ok: true }
  | { ok: false; code: "ip_not_allowed" | "session_expired" | "session_idle" | "two_factor_required"; detail: string };

/** The outcome of the network, session and two-factor checks for one request (pure: the caller supplies the facts). */
export function judge(
  policy: Security,
  facts: {
    kind: "staff" | "freelancer" | "client";
    role: string | null;
    ip: string | undefined;
    now: Date;
    /** Sessions only: when it began, when it was last used, and whether the user has two-factor on. */
    session?: { createdAt: Date; lastSeenAt: Date; twoFactor: boolean };
  },
): SecurityVerdict {
  if (facts.kind !== "client" && policy.ipAllowlist.enabled && !ipAllowed(facts.ip, policy.ipAllowlist.cidrs)) {
    return { ok: false, code: "ip_not_allowed", detail: "This network is not on your workspace's allowlist. Ask an owner to add it in Settings → Security." };
  }
  const s = facts.session;
  if (s) {
    const age = facts.now.getTime() - s.createdAt.getTime();
    if (age > policy.session.maxHours * 3_600_000) return { ok: false, code: "session_expired", detail: `Sessions last ${policy.session.maxHours} hours here. Sign in again.` };
    if (policy.session.idleMinutes > 0 && facts.now.getTime() - s.lastSeenAt.getTime() > policy.session.idleMinutes * 60_000) {
      return { ok: false, code: "session_idle", detail: `You were signed out after ${policy.session.idleMinutes} minutes without activity. Sign in again.` };
    }
    if (facts.kind !== "client" && facts.role && policy.twoFactor.roles.includes(facts.role) && !s.twoFactor) {
      return { ok: false, code: "two_factor_required", detail: "Your role must use two-factor sign-in. Turn it on in Settings → Security, then continue." };
    }
  }
  return { ok: true };
}
